// =====================================================
// Tarifario interno para presupuestos (tabla tariff_items + budget_tags)
//
// Fuente única de tarifas, costes, conceptos y etiquetas para la página
// Tarifario y el constructor de presupuestos. Se carga una vez, se comparte
// entre pantallas (useBudgetCatalog) y se actualiza al editar.
// Si la tabla está vacía o no se puede leer, se usa el tarifario 2027 estático
// (supabase/functions/_shared/budget-catalog.js), igual que el backend.
// =====================================================
import React from 'react';
import { supabase } from '../integrations/supabase/client';
import { TARIFF_CATALOG as STATIC_ITEMS, BUDGET_TAGS as STATIC_TAGS } from '../../supabase/functions/_shared/budget-catalog.js';
import { toNum } from '../../supabase/functions/_shared/budget-calc.js';

export const TAG_TONES = ['slate', 'violet', 'blue', 'teal', 'green', 'amber', 'orange', 'rose'];
export const TARIFF_UNITS = ['hora', 'ud', 'persona', 'trayecto', 'jornada', 'mes'];

const ITEM_COLUMNS = 'id, code, category, concept, unit, rate, cost, tag, note, active, sort_order, updated_at, updated_by';

function fromRow(r) {
  return {
    uuid: r.id, id: r.code, name: r.concept || '', group: r.category || 'Sin grupo', unit: r.unit || 'ud',
    rate: toNum(r.rate), cost: toNum(r.cost), tag: r.tag || '', desc: r.note || '',
    active: r.active !== false, sort: r.sort_order ?? 0, updatedAt: r.updated_at || null, updatedBy: r.updated_by || null,
  };
}

const stripCode = (t) => {
  let s = String(t.name || '').trim();
  for (const c of [t.id, t.id.replace(/^T\./, '')]) if (s.startsWith(`${c} `)) return s.slice(c.length + 1).trim();
  return s;
};

function staticState() {
  const defaultTone = (i) => TAG_TONES[i % TAG_TONES.length];
  return build(
    STATIC_ITEMS.map((t, i) => ({ uuid: null, ...t, name: stripCode(t), active: true, sort: i + 1, updatedAt: null, updatedBy: null })),
    STATIC_TAGS.map((t, i) => ({ name: t.name, desc: t.desc, tone: defaultTone(i), sort: i + 1 })),
    'static',
  );
}

function build(items, tags, source) {
  const sorted = [...items].sort((a, b) => a.sort - b.sort);
  return {
    items: sorted,
    active: sorted.filter((t) => t.active),
    byId: Object.fromEntries(sorted.map((t) => [t.id, t])),
    tags,
    tagByName: Object.fromEntries(tags.map((t) => [t.name, t])),
    source,
    loaded: source !== 'loading',
    loadedAt: Date.now(),
  };
}

let state = { ...staticState(), loaded: false, source: 'loading' };
const listeners = new Set();
let inflight = null;

function setState(next) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

export function getBudgetCatalog() { return state; }

export async function loadBudgetCatalog({ force = false } = {}) {
  if (inflight) return inflight;
  if (!force && state.loaded && state.source === 'db' && Date.now() - state.loadedAt < 60_000) return state;
  inflight = (async () => {
    try {
      const [itemsRes, tagsRes] = await Promise.all([
        supabase.from('tariff_items').select(ITEM_COLUMNS).not('code', 'is', null).order('sort_order', { ascending: true }),
        supabase.from('budget_tags').select('name, description, tone, sort_order').order('sort_order', { ascending: true }),
      ]);
      if (itemsRes.error) throw itemsRes.error;
      if (!itemsRes.data?.length) { setState(staticState()); return state; }
      const tags = !tagsRes.error && tagsRes.data?.length
        ? tagsRes.data.map((t) => ({ name: t.name, desc: t.description || '', tone: t.tone || 'slate', sort: t.sort_order ?? 0 }))
        : staticState().tags;
      setState(build(itemsRes.data.map(fromRow), tags, 'db'));
    } catch (e) {
      console.warn('[tarifario] no se pudo leer de la base de datos; se usa el estático', e);
      if (state.source !== 'db') setState(staticState());
    } finally {
      inflight = null;
    }
    return state;
  })();
  return inflight;
}

// Hook: devuelve el tarifario vigente y se re-renderiza cuando cambia.
export function useBudgetCatalog() {
  const [snap, setSnap] = React.useState(state);
  React.useEffect(() => {
    listeners.add(setSnap);
    setSnap(state);
    loadBudgetCatalog();
    return () => { listeners.delete(setSnap); };
  }, []);
  return snap;
}

// ---------- Mutaciones ----------
function replaceItem(item) {
  const items = state.items.some((t) => t.uuid === item.uuid)
    ? state.items.map((t) => (t.uuid === item.uuid ? item : t))
    : [...state.items, item];
  setState(build(items, state.tags, 'db'));
}

export async function updateTariffItem(uuid, patch) {
  const db = {};
  if ('name' in patch) db.concept = String(patch.name || '').trim();
  if ('group' in patch) db.category = String(patch.group || '').trim() || 'Sin grupo';
  if ('unit' in patch) db.unit = patch.unit || 'ud';
  if ('rate' in patch) db.rate = toNum(patch.rate);
  if ('cost' in patch) db.cost = toNum(patch.cost);
  if ('tag' in patch) db.tag = patch.tag || null;
  if ('desc' in patch) db.note = String(patch.desc || '').trim() || null;
  if ('active' in patch) db.active = !!patch.active;
  const { data, error } = await supabase.from('tariff_items').update(db).eq('id', uuid).select(ITEM_COLUMNS).maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('No tienes permiso para editar el tarifario.');
  replaceItem(fromRow(data));
  return fromRow(data);
}

export async function createTariffItem(payload, userId) {
  const code = String(payload.code || '').trim();
  if (!code) throw new Error('El código es obligatorio.');
  if (state.byId[code]) throw new Error(`Ya existe una partida con el código ${code}.`);
  const sort = (state.items.reduce((m, t) => Math.max(m, t.sort || 0), 0) || 0) + 1;
  const insert = {
    code, category: String(payload.group || '').trim() || 'Sin grupo', concept: String(payload.name || '').trim() || code,
    unit: payload.unit || 'ud', rate: toNum(payload.rate), cost: toNum(payload.cost), tag: payload.tag || null,
    note: String(payload.desc || '').trim() || null, sort_order: payload.sort ?? sort, active: true, created_by: userId || null,
  };
  const { data, error } = await supabase.from('tariff_items').insert(insert).select(ITEM_COLUMNS).maybeSingle();
  if (error) throw error;
  replaceItem(fromRow(data));
  return fromRow(data);
}

export async function deleteTariffItem(uuid) {
  const { error, count } = await supabase.from('tariff_items').delete({ count: 'exact' }).eq('id', uuid);
  if (error) throw error;
  if (!count) throw new Error('Solo un administrador puede eliminar partidas. Puedes archivarla.');
  setState(build(state.items.filter((t) => t.uuid !== uuid), state.tags, 'db'));
}

export async function fetchTariffHistory(uuid) {
  const { data, error } = await supabase.from('tariff_item_history')
    .select('id, changed_at, changed_by, old_values, new_values').eq('item_id', uuid).order('changed_at', { ascending: false }).limit(30);
  if (error) throw error;
  return data || [];
}

export async function updateBudgetTag(name, patch) {
  const db = {};
  if ('desc' in patch) db.description = String(patch.desc || '').trim() || null;
  if ('tone' in patch) db.tone = patch.tone;
  const { error } = await supabase.from('budget_tags').update(db).eq('name', name);
  if (error) throw error;
  const tags = state.tags.map((t) => (t.name === name ? { ...t, ...patch } : t));
  setState(build(state.items, tags, state.source));
}

export async function createBudgetTag({ name, desc, tone }) {
  const n = String(name || '').trim();
  if (!n) throw new Error('El nombre es obligatorio.');
  if (state.tagByName[n]) throw new Error('Esa etiqueta ya existe.');
  const sort = state.tags.reduce((m, t) => Math.max(m, t.sort || 0), 0) + 1;
  const { error } = await supabase.from('budget_tags').insert({ name: n, description: desc || null, tone: tone || 'slate', sort_order: sort });
  if (error) throw error;
  setState(build(state.items, [...state.tags, { name: n, desc: desc || '', tone: tone || 'slate', sort }], state.source));
}
